import ENV from "@/utils/env";
import { api } from "@/lib/api";
import { Study, StudyPartial } from "./types/study.type";
import { PaginatedReq, PaginatedResp } from "../types/paginated.type";

/* Request Types */
export interface GetStudiesReq extends PaginatedReq {
  query?: string;
  sortBy?: "asc" | "desc";
  orderBy?: "name" | "updatedAt";
  view?: "all" | "archived";
}

export interface CreateStudyReq {
  projectId: string;
  values: {
    name: string;
    type: "asset" | "scene" | "video";
  };
}

export interface UpdateStudyReq {
  projectId: string;
  studyId: string;
  backgroundUpdate?: boolean;
  values: StudyPartial;
}

export interface DeleteStudyReq {
  projectId: string;
  studyId: string;
}

/* Response Types */
export interface GetStudiesResp extends PaginatedResp {
  studies: Array<Study>;
}

export interface CreateStudyResp {
  study: Study;
}

export interface GetStudyResp {
  study: Study;
}

export interface UpdateStudyResp {
  study: Study;
}

export interface DeleteStudyResp {
  id: string;
}

/**
 * getProjectStudies endpoint
 *
 * Get all studies for a given projectId with optional filtering parameters.
 *
 * @param projectId - The project ID to fetch studies for
 * @param params - Optional filtering parameters
 */
export const getProjectStudies = async (projectId: string, params: GetStudiesReq) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies`, { params });
  return resp.data as GetStudiesResp;
};

/**
 * getAccountStudies endpoint
 *
 * Get all studies for a given account with optional filtering parameters.
 *
 * @param params - Optional filtering parameters
 */
export const getAccountStudies = async (params: GetStudiesReq) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/studies`, { params });
  return resp.data as GetStudiesResp;
};

/**
 * createStudy endpoint
 *
 * Create a new study against a project.
 *
 * @param params - Object containing projectId and values
 * @param params.projectId - The project ID to create the study in
 * @param params.values - The study creation values
 */
export const createStudy = async ({ projectId, values }: CreateStudyReq) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies`, values);
  return resp.data as CreateStudyResp;
};

/**
 * getStudy endpoint
 *
 * Get a study from a project by its id.
 *
 * @param projectId - The project ID
 * @param studyId - The study ID to fetch
 */
export const getStudy = async (projectId: string, studyId: string) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}`);
  return resp.data as GetStudyResp;
};

/**
 * updateStudy endpoint
 *
 * Update a specific study.
 *
 * @param params - Object containing projectId, studyId and values
 * @param params.projectId - The project ID
 * @param params.studyId - The study ID to update
 * @param params.values - The study update values
 */
export const updateStudy = async ({ projectId, studyId, values }: UpdateStudyReq) => {
  const resp = await api.put(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}`, values);
  return resp.data as UpdateStudyResp;
};

/**
 * deleteStudy endpoint
 *
 * Delete a specific study from a project.
 *
 * @param params - Object containing projectId and studyId
 * @param params.projectId - The project ID
 * @param params.studyId - The study ID to delete
 */
export const deleteStudy = async ({ projectId, studyId }: DeleteStudyReq) => {
  const resp = await api.delete(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}`);
  return resp.data as DeleteStudyResp;
};
